import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  InputSignal,
  ModelSignal,
  OutputEmitterRef,
  Signal,
  computed,
  input,
  model,
  output,
} from '@angular/core';
import { ControlValueAccessor, ReactiveFormsModule } from '@angular/forms';

import { TypedTemplateDirective, provideValueAccessor } from '@core/shared/util';
import {
  AutocompleteInputAppearance,
  AutocompleteInputComponent,
  AutocompletePanelComponent,
  SelectOption,
  SpinnerComponent,
} from '@core/ui';
import { PlatformSupplierBasic, supplierCategoryDescriptions } from '@mp/supplier-management/common/domain';

import { SupplierIconComponent } from '../supplier-icon/supplier-icon.component';

@Component({
  selector: 'mpsm-supplier-autocomplete-input',
  templateUrl: './supplier-autocomplete-input.component.html',
  styleUrl: './supplier-autocomplete-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,

    SupplierIconComponent,
    AutocompleteInputComponent,
    AutocompletePanelComponent,
    TypedTemplateDirective,
    SpinnerComponent,
  ],
  providers: [provideValueAccessor(SupplierAutocompleteInputComponent)],
})
export class SupplierAutocompleteInputComponent implements ControlValueAccessor {
  @HostBinding() readonly class = 'mpsm-supplier-autocomplete-input';

  readonly suppliers: InputSignal<PlatformSupplierBasic[]> = input<PlatformSupplierBasic[]>([]);
  readonly isLoading: InputSignal<boolean> = input<boolean>(false);

  readonly appearance: InputSignal<AutocompleteInputAppearance> = input<AutocompleteInputAppearance>('fill');

  readonly inline: InputSignal<boolean> = input<boolean>(false);
  readonly disabled: InputSignal<boolean> = input<boolean>(false);

  readonly label: InputSignal<string> = input<string>('Übergeordneter Lieferant');
  readonly placeholder: InputSignal<string> = input<string>('Plattformlieferanten zuordnen');
  readonly icon: InputSignal<string> = input<string>('local_shipping');

  readonly value: ModelSignal<PlatformSupplierBasic | undefined> = model<PlatformSupplierBasic | undefined>(undefined);

  readonly options: Signal<SelectOption<PlatformSupplierBasic>[]> = computed(() =>
    this.mapSuppliersToSelectOptions(this.suppliers()),
  );

  readonly valueChanges: OutputEmitterRef<PlatformSupplierBasic | undefined> = output<
    PlatformSupplierBasic | undefined
  >();

  readonly searchTermChange: OutputEmitterRef<string> = output<string>();

  touched = false;

  onTouched: () => void = () => {};
  onChange: (value: PlatformSupplierBasic | undefined) => void = () => {};

  updateValue(value: PlatformSupplierBasic | undefined): void {
    if (!this.disabled()) {
      this.writeValue(value);
      this.onChange(value);
    }
  }

  /* ControlValueAccessor Methods */

  writeValue(value: PlatformSupplierBasic | undefined): void {
    this.value.set(value);
    this.valueChanges.emit(value);
  }

  registerOnChange(fn: (value: PlatformSupplierBasic | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  private mapSuppliersToSelectOptions(suppliers: PlatformSupplierBasic[]): SelectOption<PlatformSupplierBasic>[] {
    return suppliers.map((supplier) => ({
      label: supplier.name,
      subLabel: this.getSupplierOptionSubLabel(supplier),
      value: supplier,
    }));
  }

  private getSupplierOptionSubLabel({ category, externalNumber }: PlatformSupplierBasic): string | undefined {
    if (category && externalNumber) {
      return `${supplierCategoryDescriptions[category]} ${externalNumber}`;
    }
    if (category) {
      return supplierCategoryDescriptions[category];
    }
    if (externalNumber) {
      return externalNumber;
    }
    return undefined;
  }
}
